import React, { useEffect, useState } from "react";
import Filter from "./Filter";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckBox from "../../../components/CheckBox";
import { ReactComponent as AwardIcon } from "../../../assets/Icons/award.svg";
import { useFees } from "../../../contexts/Fees";
import { SUCCESS } from "../../../constants/common";
import FormControlLabel from "@mui/material/FormControlLabel";

const InsurancePlansFilter = ({
  setFilters,
  isMobileView,
  goBack,
  filters = {},
}) => {
  const { fetchInsurances, insurances, fetchInsurancesStatus } = useFees();
  const [search, setSearch] = useState("");
  const [expandedInsurancePlans, expandInsurancePlans] = useState(false);
  const [showInsuranceSection, setShowInsuranceSection] = useState(
    Boolean(filters?.insurance?.length)
  );

  const privatePayChecked = Boolean(filters?.private_pay_only);
  const selectedInsurances = filters?.insurance || [];

  useEffect(() => {
    if (selectedInsurances.length > 0) {
      setShowInsuranceSection(true);
    }
  }, [selectedInsurances]);

  const handleInsuranceToggle = (checked) => {
    setShowInsuranceSection(checked);
    if (!checked) {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.insurance;
        return newFilters;
      });
    }
  };

  const handlePrivatePayToggle = (checked) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (checked) {
        newFilters.private_pay_only = true;
      } else {
        delete newFilters.private_pay_only;
      }
      return newFilters;
    });
  };

  const toggleInsurancePlan = (planId) => {
    const stringId = planId.toString();
    setFilters(prev => {
      const currentInsurances = prev.insurance || [];
      const newInsurance = currentInsurances.includes(stringId)
        ? currentInsurances.filter(id => id !== stringId)
        : [...currentInsurances, stringId];
      
      return {
        ...prev,
        insurance: newInsurance.length ? newInsurance : undefined,
      };
    });
  };

  const toggleExpand = () => expandInsurancePlans(!expandedInsurancePlans);

  // Payers are jurisdiction specific, so the list follows the state being searched.
  // With no state chosen the full national list is returned, as before.
  const searchedStateId = filters?.state;

  useEffect(() => {
    fetchInsurances(searchedStateId ? { state_id: searchedStateId } : {});
  }, [searchedStateId]);

  // Changing state can retire a payer that is still selected. Left alone it would
  // keep filtering results while its checkbox is no longer on screen.
  useEffect(() => {
    if (fetchInsurancesStatus !== SUCCESS || !selectedInsurances.length) {
      return;
    }

    // Only prune once the loaded list is actually the one for this state. The
    // context is shared with the profile drawer, so an unrelated list can still
    // be in hand for a render or two after the state changes.
    if (!searchedStateId) {
      return;
    }

    const loadedStateId = insurances.find((item) => item?.state_id)?.state_id;

    if (String(loadedStateId ?? "") !== String(searchedStateId)) {
      return;
    }

    const available = new Set(insurances.map((item) => item.id.toString()));
    const stillValid = selectedInsurances.filter((id) => available.has(id));

    if (stillValid.length !== selectedInsurances.length) {
      setFilters((prev) => ({
        ...prev,
        insurance: stillValid.length ? stillValid : undefined,
      }));
    }
  }, [insurances, fetchInsurancesStatus, searchedStateId]);

  const filteredInsurances = insurances.filter(insurance =>
    insurance.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Filter
      label="Payment Options"
      icon={<AwardIcon />}
      expandedDefault={!isMobileView}
      isRigntIcon={isMobileView}
      goBack={goBack}
    >
      {!isMobileView && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          <FormControlLabel
            control={
              <CheckBox
                checked={showInsuranceSection}
                onChange={(e) => handleInsuranceToggle(e.target.checked)}
              />
            }
            label="Insurance Plans"
            sx={{ gap: 1.5, mx: 0 }}
          />
          <FormControlLabel
            control={
              <CheckBox
                checked={privatePayChecked}
                onChange={(e) => handlePrivatePayToggle(e.target.checked)}
              />
            }
            label="Private Pay"
            sx={{ gap: 1.5, mx: 0 }}
          />
        </Box>
      )}

      {showInsuranceSection && (
        <>
          {!isMobileView && (
            <Typography variant="body2" fontWeight={600}>
              Insurance Plans
            </Typography>
          )}

          <TextField
            fullWidth
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              "input:not(textarea)": {
                pt: { xs: 1.75, sm: 1.5 },
                pb: 1.5,
                fontSize: { xs: 12, sm: 14 },
                width: "90%",
                px: 1,
                "&::placeholder": { fontWeight: 700 },
                height: isMobileView ? "40px" : "25px",
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxHeight: expandedInsurancePlans ? "none" : 90,
              overflow: "hidden",
              pt: isMobileView ? 2 : 0,
            }}
          >
            {filteredInsurances.map((insurance) => {
              const insuranceId = insurance.id.toString();
              return (
                <CheckBox
                  key={insuranceId}
                  label={insurance.name}
                  checked={selectedInsurances.includes(insuranceId)}
                  onChange={() => toggleInsurancePlan(insurance.id)}
                />
              );
            })}
          </Box>

          <Button
            onClick={toggleExpand}
            disableRipple
            sx={{
              p: 0,
              justifyContent: "flex-start",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            {expandedInsurancePlans ? "See less" : "See more"}
          </Button>
        </>
      )}
    </Filter>
  );
};

export default InsurancePlansFilter;